import React from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';

const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r, g, b };
};

const rgbToHex = ({ r, g, b }) =>
    '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

const rgbToHsv = ({ r, g, b }) => {
    const rn = r / 255, gn = g / 255, bn = b / 255;
    const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn), d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === rn) h = ((gn - bn) / d + 6) % 6;
        else if (max === gn) h = (bn - rn) / d + 2;
        else h = (rn - gn) / d + 4;
        h = Math.round(h * 60);
    }
    return { h, s: Math.round(max === 0 ? 0 : (d / max) * 100), v: Math.round(max * 100) };
};

const hsvToHex = ({ h, s, v }) => {
    const s1 = s / 100, v1 = v / 100;
    const f = (n) => { const k = (n + h / 60) % 6; return v1 - v1 * s1 * Math.max(0, Math.min(k, 4 - k, 1)); };
    return rgbToHex({ r: f(5) * 255, g: f(3) * 255, b: f(1) * 255 });
};

const TABS = ['HEX', 'RGB', 'HSV'];

const ColorPicker = ({ value, onChange, onOpen, className, title, style, icon }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [tab, setTab] = React.useState('HEX');
    const [hexInput, setHexInput] = React.useState(value || '');
    const [pos, setPos] = React.useState({ top: 0, left: 0 });
    const btnRef = React.useRef(null);
    const popoverRef = React.useRef(null);

    React.useEffect(() => { setHexInput(value || ''); }, [value]);

    React.useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        const handleScroll = () => {
            const rect = btnRef.current?.getBoundingClientRect();
            if (rect) setPos(calcPos(rect));
        };
        document.addEventListener('mousedown', handleClick);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen]);

    const calcPos = (rect) => {
        const PICKER_HEIGHT = 300;
        const PICKER_WIDTH = 220;
        const left = Math.min(rect.left, window.innerWidth - PICKER_WIDTH - 8);
        if (window.innerHeight - rect.bottom < PICKER_HEIGHT && rect.top > window.innerHeight - rect.bottom) {
            return { bottom: window.innerHeight - rect.top + 2, top: undefined, left };
        }
        return { top: rect.bottom + 2, bottom: undefined, left };
    };

    const handleOpen = (e) => {
        e.stopPropagation();
        setPos(calcPos(btnRef.current.getBoundingClientRect()));
        const opening = !isOpen;
        setIsOpen(v => !v);
        if (opening) onOpen?.();
    };

    const emit = (hex) => onChange({ target: { value: hex } });

    const handlePickerChange = (hex) => { setHexInput(hex); emit(hex); };

    const handleHexInput = (e) => {
        const v = e.target.value;
        setHexInput(v);
        if (/^#[0-9a-fA-F]{6}$/.test(v)) emit(v);
    };

    const rgb = /^#[0-9a-fA-F]{6}$/.test(value) ? hexToRgb(value) : { r: 0, g: 0, b: 0 };
    const hsv = /^#[0-9a-fA-F]{6}$/.test(value) ? rgbToHsv(rgb) : { h: 0, s: 0, v: 0 };

    const handleRgb = (channel, raw) => {
        const n = Math.max(0, Math.min(255, parseInt(raw) || 0));
        emit(rgbToHex({ ...rgb, [channel]: n }));
    };

    const handleHsv = (channel, raw) => {
        const max = channel === 'h' ? 360 : 100;
        const n = Math.max(0, Math.min(max, parseInt(raw) || 0));
        emit(hsvToHex({ ...hsv, [channel]: n }));
    };

    const handleEyedropper = async () => {
        if (!window.EyeDropper) return;
        try {
            const result = await new window.EyeDropper().open();
            setHexInput(result.sRGBHex);
            emit(result.sRGBHex);
        } catch {}
    };

    const inputCls = "w-full text-[11px] px-1 py-1 border border-gray-300 bg-white text-coal font-mono outline-none focus:border-gray-400 text-center";

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={handleOpen}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                title={title}
                className={`p-0 border border-black/50 cursor-pointer flex items-center justify-center overflow-hidden ${className || ''}`}
                style={{ backgroundColor: value, ...style }}
            >
                {icon}
            </button>
            {isOpen && createPortal(
                <div
                    ref={popoverRef}
                    style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, zIndex: 9999 }}
                    className="bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.5)] p-2 flex flex-col gap-2"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <HexColorPicker color={value || '#ffffff'} onChange={handlePickerChange} />

                    <div className="flex items-start gap-2">
                        {window.EyeDropper && (
                            <button
                                type="button"
                                onClick={handleEyedropper}
                                title="Pick colour from screen"
                                className="w-6 h-6 rounded-full border border-gray-400 shrink-0 hover:scale-110 transition-transform mt-0.5"
                                style={{ backgroundColor: value }}
                            />
                        )}
                        <div className="flex-1 flex flex-col gap-1">
                            <select
                                value={tab}
                                onChange={(e) => setTab(e.target.value)}
                                className="w-full text-[11px] px-1 py-1 border border-gray-300 bg-white text-coal font-mono outline-none focus:border-gray-400 cursor-pointer"
                            >
                                {TABS.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            {tab === 'HEX' && (
                                <input
                                    type="text"
                                    value={hexInput}
                                    onChange={handleHexInput}
                                    maxLength={7}
                                    spellCheck={false}
                                    placeholder="#000000"
                                    className={inputCls}
                                />
                            )}
                            {tab === 'RGB' && (
                                <div className="flex gap-1">
                                    {['r', 'g', 'b'].map(ch => (
                                        <div key={ch} className="flex-1 flex flex-col gap-0.5">
                                            <input type="number" min={0} max={255} value={rgb[ch]} onChange={(e) => handleRgb(ch, e.target.value)} className={inputCls} />
                                            <span className="text-[9px] font-['Jersey_10'] uppercase text-center text-coal/60">{ch}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {tab === 'HSV' && (
                                <div className="flex gap-1">
                                    {[['h', 360], ['s', 100], ['v', 100]].map(([ch, max]) => (
                                        <div key={ch} className="flex-1 flex flex-col gap-0.5">
                                            <input type="number" min={0} max={max} value={hsv[ch]} onChange={(e) => handleHsv(ch, e.target.value)} className={inputCls} />
                                            <span className="text-[9px] font-['Jersey_10'] uppercase text-center text-coal/60">{ch}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ColorPicker;
